/**
 * OfflineIndicator - Shows connectivity + sync status in the top bar
 *
 * Only renders in desktop (Tauri) mode. Web mode always has
 * the browser's built-in offline indicator.
 *
 * States:
 * - Online + idle:     hidden (no visual noise)
 * - Online + syncing:  blue dot + "Syncing..."
 * - Online + error:    red dot + "Sync error"
 * - Offline:           amber dot + "Offline"
 */

import { useState, useEffect } from 'react';
import { isDesktop, isOnline, onConnectivityChange } from '../lib/db/dataLayer';
import {
  getSyncStatus,
  onSyncStatusChange,
  type SyncState,
} from '../lib/db/syncEngine';
import './OfflineIndicator.css';

export function OfflineIndicator() {
  const [online, setOnline] = useState(true);
  const [desktop, setDesktop] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const isDesktopMode = isDesktop();
    setDesktop(isDesktopMode);

    if (!isDesktopMode) return;

    // Sync initial state
    setOnline(isOnline());
    const status = getSyncStatus();
    setSyncState(status.state);
    setPending(status.pending);

    // Listen for connectivity changes
    const unsubConn = onConnectivityChange((nowOnline) => {
      setOnline(nowOnline);
    });

    // Listen for sync status changes
    const unsubSync = onSyncStatusChange((status) => {
      setSyncState(status.state);
      setPending(status.pending);
    });

    return () => {
      unsubConn();
      unsubSync();
    };
  }, []);

  // Don't render anything in web mode
  if (!desktop) return null;

  // Don't render when online and idle with no pending items
  if (online && syncState === 'idle' && pending === 0) return null;

  // Determine display state
  let dotClass = 'offline-indicator__dot';
  let label = '';

  if (!online) {
    dotClass += ' offline-indicator__dot--offline';
    label = 'Offline';
  } else if (syncState === 'syncing') {
    dotClass += ' offline-indicator__dot--syncing';
    label = pending > 0 ? `Syncing ${pending}...` : 'Syncing...';
  } else if (syncState === 'error') {
    dotClass += ' offline-indicator__dot--error';
    label = 'Sync error';
  } else if (pending > 0) {
    dotClass += ' offline-indicator__dot--pending';
    label = `${pending} pending`;
  } else {
    return null;
  }

  return (
    <div
      className="offline-indicator"
      role="status"
      aria-live="polite"
      aria-label={`${label}. ${!online ? 'Results will be saved locally and synced when reconnected.' : ''}`}
    >
      <span className={dotClass} />
      <span className="offline-indicator__label">{label}</span>
    </div>
  );
}
